import React, { useContext, useState } from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChartToolCall, ToolCallHandlersContext, ChartAutoRunContext } from '../assistant-ui/chart-tool-call';
import { SimilarColumnValuesToolCall } from '../assistant-ui/similar-column-values-tool-call';
import { ContextToolCall } from '../assistant-ui/context-tool-call';
import { SemanticLayerContextToolCall } from '../assistant-ui/semantic-layer-context-tool-call';
import { SourceTablesToolCall } from '../assistant-ui/source-tables-tool-call';
import { ApplyChangesToolCall } from '../assistant-ui/apply-changes-tool-call';
import { ResetWorkspaceToolCall } from '../assistant-ui/reset-workspace-tool-call';
import { CreateMetricToolCall } from '../assistant-ui/create-metric-tool-call';
import { ProcessMetricsToolCall } from '../assistant-ui/process-metrics-tool-call';
import { GetMetricJoinPathsToolCall } from '../assistant-ui/get-metric-join-paths-tool-call';
import { GetSampleDataToolCall } from '../assistant-ui/get-sample-data-tool-call';
import { GetColumnDistributionToolCall } from '../assistant-ui/get-column-distribution-tool-call';
import { CreateRelationshipToolCall } from '../assistant-ui/create-relationship-tool-call';
import { CreateDimensionToolCall } from '../assistant-ui/create-dimension-tool-call';
import { MapStagingToEntityToolCall } from '../assistant-ui/map-staging-to-entity-tool-call';
import { CreateEntityToolCall } from '../assistant-ui/create-entity-tool-call';
import { BuildNodesToolCall } from '../assistant-ui/build-nodes-tool-call';
import { CreateMultiSourceStagingNodeToolCall } from '../assistant-ui/create-multi-source-staging-node-tool-call';
import { CreateSingleSourceStagingNodeToolCall } from '../assistant-ui/create-single-source-staging-node-tool-call';
import { SetMetricJoinPathsToolCall } from '../assistant-ui/set-metric-join-paths-tool-call';
import { RunFullBuildToolCall } from '../assistant-ui/run-full-build-tool-call';
import { FileUploadToolCall } from '../assistant-ui/file-upload-tool-call';
import { ChoiceToolCall } from '../assistant-ui/choice-tool-call';
import { OutputTruncatedToolCall } from '../assistant-ui/output-truncated-tool-call';
import { GetStagingNodesToolCall } from '../assistant-ui/get-staging-nodes-tool-call';
import { SearchUnstructuredContextToolCall } from '../assistant-ui/search-unstructured-context-tool-call';
import { GetUnstructuredContextToolCall } from '../assistant-ui/get-unstructured-context-tool-call';
import { ExecuteSqlQueryToolCall } from '../assistant-ui/execute-sql-query-tool-call';
import { CreateEllieArtifactToolCall } from '../assistant-ui/create-ellie-artifact-tool-call';
import { AppendToEllieArtifactToolCall } from '../assistant-ui/append-to-ellie-artifact-tool-call';
import { DashboardToolCall } from '../assistant-ui/dashboard-tool-call';
import { DashboardReadToolCall } from '../assistant-ui/dashboard-read-tool-call';
import { RequestHelpToolCall } from '../assistant-ui/request-help-tool-call';
import { CompleteValidationToolCall } from '../assistant-ui/complete-validation-tool-call';
import { CompleteDataModelingToolCall } from '../assistant-ui/complete-data-modeling-tool-call';
import { KBToolCall } from '../assistant-ui/kb-tool-call';
import { GapToolCall } from '../assistant-ui/gap-tool-call';
import { RequestToolCall, RequestLookupToolCall } from '../assistant-ui/request-tool-call';
import { VideoToolCall } from '../assistant-ui/video-tool-call';
import { GoalToolCall } from '../assistant-ui/goal-tool-call';
import { ListUseCasesToolCall } from '../assistant-ui/list-use-cases-tool-call';
import { CompleteUseCaseDiscoveryToolCall } from '../assistant-ui/complete-use-case-discovery-tool-call';
import { Button } from '../ui/button';
import { ThreadSettingsContext, isVisibleInConciseMode } from './threadSettings';

interface ToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

// Registry for custom tool components
const toolComponentRegistry: Record<string, React.ComponentType<any>> = {
  generate_chart_input: ChartToolCall,
  create_dashboard: DashboardToolCall,
  update_dashboard: DashboardToolCall,
  // Lookups, so they render quietly and drop out in concise mode.
  get_dashboard: DashboardReadToolCall,
  get_dashboard_data: DashboardReadToolCall,
  generate_and_run_analysis: ChartToolCall,
  similar_column_values: SimilarColumnValuesToolCall,
  get_similar_column_values: SimilarColumnValuesToolCall,
  create_context_item: ContextToolCall,
  // Ruthie (Data Architect) tools
  get_semantic_layer_context: SemanticLayerContextToolCall,
  get_source_tables: SourceTablesToolCall,
  get_staging_nodes: GetStagingNodesToolCall,
  apply_changes_to_develop: ApplyChangesToolCall,
  reset_workspace: ResetWorkspaceToolCall,
  create_metric: CreateMetricToolCall,
  process_metrics: ProcessMetricsToolCall,
  get_metric_join_paths: GetMetricJoinPathsToolCall,
  get_sample_data_from_node: GetSampleDataToolCall,
  get_column_distribution: GetColumnDistributionToolCall,
  create_relationship: CreateRelationshipToolCall,
  create_dimension: CreateDimensionToolCall,
  map_staging_to_entity: MapStagingToEntityToolCall,
  create_entity: CreateEntityToolCall,
  build_nodes: BuildNodesToolCall,
  create_multi_source_staging_node: CreateMultiSourceStagingNodeToolCall,
  create_single_source_staging_node: CreateSingleSourceStagingNodeToolCall,
  set_metric_join_paths: SetMetricJoinPathsToolCall,
  run_full_build: RunFullBuildToolCall,
  request_file_upload: FileUploadToolCall,
  // Clarifying questions rendered as radios / checkboxes (ENG-82)
  ask_user_choice: ChoiceToolCall,
  // Raised by the agent graph when a response is cut off at the output-token limit
  resolve_output_truncation: OutputTruncatedToolCall,
  // Ellie unstructured context tools
  search_unstructured_context: SearchUnstructuredContextToolCall,
  get_unstructured_context: GetUnstructuredContextToolCall,
  // Ellie SQL and artifact tools
  execute_sql_query: ExecuteSqlQueryToolCall,
  create_ellie_artifact: CreateEllieArtifactToolCall,
  append_to_ellie_artifact: AppendToEllieArtifactToolCall,
  // EDA validation tools
  complete_data_source_validation: CompleteValidationToolCall,
  // Data modeling tools
  complete_data_modeling: CompleteDataModelingToolCall,
  // Knowledge base tools
  search_kb: KBToolCall,
  get_kb_article: KBToolCall,
  create_kb_article: KBToolCall,
  update_kb_article: KBToolCall,
  create_kb_folder: KBToolCall,
  move_kb_item: KBToolCall,
  list_kb_folder: KBToolCall,
  // Use case tools
  list_use_cases: ListUseCasesToolCall,
  complete_use_case_discovery: CompleteUseCaseDiscoveryToolCall,
  // Semantic gap tools
  flag_semantic_gap: GapToolCall,
  list_semantic_gaps: GapToolCall,
  // Request help (log a task for the data team)
  request_help: RequestHelpToolCall,
  // Support requests — a real thread with the Pliable team. The two write tools
  // survive concise mode; the two lookups don't (see request-tool-call.tsx).
  create_support_request: RequestToolCall,
  reply_to_request: RequestToolCall,
  list_my_requests: RequestLookupToolCall,
  get_request: RequestLookupToolCall,
  // Video / Fireflies tools
  ingest_video: VideoToolCall,
  get_video_transcript: VideoToolCall,
  search_video_transcript: VideoToolCall,
  summarize_video: VideoToolCall,
  extract_key_findings: VideoToolCall,
  create_clip: VideoToolCall,
  // Goals
  list_goals: GoalToolCall,
};

export const ToolCall = React.memo<ToolCallProps>(({
  toolName,
  toolCallId,
  args,
  argsText,
  result,
  status,
}) => {
  const handlers = useContext(ToolCallHandlersContext);
  const autoRunFromContext = useContext(ChartAutoRunContext);
  const threadSettings = useContext(ThreadSettingsContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Check if there's a custom component for this tool
  const CustomComponent = toolComponentRegistry[toolName];

  // At low verbosity, behind-the-scenes tool calls render NOTHING in any
  // state (running, complete, or errored) — only components explicitly marked
  // with showInConciseMode() (charts, artifacts, file uploads, ...) stay
  // visible. The Thread-level loading indicator covers the waiting state, and
  // the assistant's text carries the result (or explains a failure).
  if (threadSettings?.verbosity === 'low' && !isVisibleInConciseMode(CustomComponent)) {
    return null;
  }

  if (CustomComponent) {
    return (
      <CustomComponent
        toolName={toolName}
        toolCallId={toolCallId}
        args={args}
        argsText={argsText}
        result={result}
        status={status}
      />
    );
  }

  // Default tool call component
  return (
    <div className={cn(
      "rounded-lg border p-4 mb-4",
      isRunning && "border-muted bg-muted/50",
      isComplete && "border-success/50 bg-success/10",
      isError && "border-destructive/50 bg-destructive/10"
    )}>
      <div className="flex items-center gap-2 mb-2">
        {isRunning && <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />}
        {isComplete && <CheckCircle2Icon className="h-4 w-4 text-success" />}
        {isError && <XCircleIcon className="h-4 w-4 text-destructive" />}
        <span className="text-sm font-medium">
          {isRunning && 'Running tool: '}
          {isComplete && 'Tool completed: '}
          {isError && 'Tool error: '}
          <strong>{toolName}</strong>
        </span>
        {!isRunning && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-6 px-2"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="h-3 w-3 mr-1" />
                Hide
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-3 w-3 mr-1" />
                Show
              </>
            )}
          </Button>
        )}
      </div>
      {isExpanded && (
        <div className="mt-3 space-y-2">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Arguments:</p>
            <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
              {argsText}
            </pre>
          </div>
          {result !== undefined && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Result:</p>
              <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
                {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
          {isError && status?.reason && (
            <div>
              <p className="text-xs font-medium text-destructive mb-1">Error:</p>
              <p className="text-xs text-destructive">{status.reason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return (
    prev.toolCallId === next.toolCallId &&
    prev.toolName === next.toolName &&
    prev.result === next.result &&
    prev.status?.type === next.status?.type &&
    prev.argsText === next.argsText
  );
});
ToolCall.displayName = 'ToolCall';

